<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useWorkspaceStore } from '@/stores/workspace'
import { adapter } from '@/services/prototype-adapter'
import { formatDateTime } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'

const props = withDefaults(defineProps<{
  kind?: string
  unknownPath?: string
}>(), { kind: 'not-found', unknownPath: '' })

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ui = useUiStore()
const workspace = useWorkspaceStore()
const checking = ref(false)
const requestNo = `REQ-20261017-${String(Math.floor(Math.random() * 9000) + 1000)}`
const recentTabs = computed(() => workspace.tabs.filter(tab => !tab.pinned && tab.path !== route.path).slice(-4).reverse())

const content = computed(() => {
  // Reading the checking flag makes maintenance copy refresh after a retry.
  void checking.value
  switch (props.kind) {
    case 'forbidden':
      return {
        code: '403',
        tone: 'warning',
        icon: 'lock',
        title: '当前角色没有访问权限',
        text: '该页面需要更高的角色权限或更宽的数据范围。你的登录状态仍然有效，可以返回工作台或联系超级管理员调整授权。',
        meta: `需要权限：${String(route.query.need || '未声明')} · 来源：${String(route.query.from || route.fullPath)}`,
      }
    case 'server-error':
      return {
        code: '500',
        tone: 'danger',
        icon: 'server',
        title: '原型交互暂时不可用',
        text: '当前页面状态没有正常返回。可以重试加载，或返回工作台继续处理其他业务；请求上下文已经保留。',
        meta: `请求编号：${requestNo} · 未写入任何数据库`,
      }
    case 'maintenance':
      return {
        code: 'MAINT',
        tone: 'info',
        icon: 'wrench',
        title: '平台维护窗口',
        text: adapter.settings.maintenanceMessage || '维护期间健康数据上报和管理端写入会暂停，已登录会话保留，恢复后可继续操作。',
        meta: `预计恢复：${formatDateTime(adapter.settings.maintenanceUntil)} · 状态：${adapter.settings.maintenanceEnabled ? '维护中' : '已恢复'}`,
      }
    case 'offline':
      return {
        code: 'OFFLINE',
        tone: 'warning',
        icon: 'wifi-off',
        title: '网络连接已断开',
        text: '当前页面与列表数据仍保留在浏览器内存中。恢复网络后可继续操作，不会丢失尚未提交的表单内容。',
        meta: '原型网络状态监听中 · 数据未离开当前浏览器',
      }
    case 'not-found':
    default:
      return {
        code: '404',
        tone: 'info',
        icon: 'compass',
        title: '没有找到这个页面',
        text: '地址可能已变更，或当前链接来自已下线的功能。可以使用全局搜索定位页面，或返回工作台重新开始。',
        meta: `未匹配地址：${props.unknownPath || route.fullPath}`,
      }
  }
})

function back(): void {
  if (window.history.length > 1) router.back()
  else void router.replace('/dashboard')
}

function goHome(): void {
  void router.replace(auth.isAuthenticated ? '/dashboard' : '/login')
}

function openSearch(): void {
  ui.commandOpen = true
}

function openRecent(path: string): void {
  void router.replace(path)
}

async function requestAccess(): Promise<void> {
  const need = String(route.query.need || '页面访问权限')
  try {
    await ElMessageBox.alert(
      `请向超级管理员说明业务用途，并申请权限「${need}」。当前登录身份为${auth.profile?.name ?? '管理员'}（${auth.roleName}）。授权变更后重新进入本页即可生效。`,
      '申请访问权限',
      { confirmButtonText: '知道了', type: 'info' },
    )
  } catch {
    // Closing the advisory dialog keeps the recoverable 403 surface.
  }
}

function contactSupport(): void {
  ui.helpOpen = true
  ElMessage.info('已打开帮助中心，可搜索“权限申请”“登录过期”或“服务异常”获取处理步骤')
}

function retryCurrent(): void {
  const redirect = typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/') ? route.query.redirect : ''
  if (redirect) void router.replace(redirect)
  else window.location.reload()
}

async function retry(): Promise<void> {
  checking.value = true
  await new Promise(resolve => setTimeout(resolve, 520))
  checking.value = false
  if (props.kind === 'offline' && !navigator.onLine) {
    ElMessage.warning('网络尚未恢复，请检查连接后重试')
    return
  }
  if (props.kind === 'server-error') {
    ElMessage.success('请求已重新发起，当前原型状态已恢复')
    void router.replace('/dashboard')
    return
  }
  if (props.kind === 'maintenance') {
    if (adapter.settings.maintenanceEnabled) {
      ElMessage.info(`维护窗口尚未结束，预计 ${formatDateTime(adapter.settings.maintenanceUntil)} 恢复`)
      return
    }
    ElMessage.success('维护模式已关闭，正在恢复业务页面')
    const redirect = typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/')
      ? route.query.redirect
      : '/dashboard'
    void router.replace(redirect)
    return
  }
  window.location.reload()
}

function openGeneralSettings(): void {
  void router.replace({ path: '/settings/general', hash: '#about' })
}

function copyRequest(): void {
  void navigator.clipboard?.writeText(requestNo).then(() => ElMessage.success('请求编号已复制')).catch(() => ElMessage.info(requestNo))
}

function online(): void {
  if (props.kind === 'offline' && navigator.onLine) {
    ElMessage.success('网络已恢复')
    void router.replace('/dashboard')
  }
}

onMounted(() => window.addEventListener('online', online))
onBeforeUnmount(() => window.removeEventListener('online', online))
</script>

<template>
  <div class="state-page" :class="{ 'state-page--embedded': auth.isAuthenticated }">
    <div class="state-card">
      <div class="state-card__code" :class="`state-card__code--${content.tone}`">
        <AppIcon :name="content.icon" :size="26" style="margin: 0 auto 8px" />
        {{ content.code }}
      </div>
      <h1 class="state-card__title">{{ content.title }}</h1>
      <p class="state-card__text">{{ content.text }}</p>
      <div class="state-card__meta">{{ content.meta }}</div>

      <div class="state-card__actions">
        <template v-if="kind === 'forbidden'">
          <button type="button" class="btn btn--primary" @click="goHome"><AppIcon name="dashboard" :size="14" />返回工作台</button>
          <button type="button" class="btn" @click="back">返回上一页</button>
          <button type="button" class="btn" @click="openSearch"><AppIcon name="search" :size="14" />搜索可访问页面</button>
          <button type="button" class="btn" @click="requestAccess"><AppIcon name="shield" :size="14" />申请权限</button>
        </template>
        <template v-else-if="kind === 'not-found'">
          <button type="button" class="btn btn--primary" @click="goHome"><AppIcon name="dashboard" :size="14" />返回工作台</button>
          <button type="button" class="btn" @click="openSearch"><AppIcon name="search" :size="14" />全局搜索</button>
          <button v-for="tab in recentTabs" :key="tab.key" type="button" class="btn" @click="openRecent(tab.fullPath)">
            <AppIcon name="history" :size="14" />{{ tab.title }}
          </button>
        </template>
        <template v-else-if="kind === 'offline'">
          <button type="button" class="btn btn--primary" :disabled="checking" @click="retry"><AppIcon name="refresh" :size="14" />{{ checking ? '正在检测…' : '重新检测网络' }}</button>
          <button type="button" class="btn" @click="retryCurrent"><AppIcon name="arrow-right" :size="14" />重试当前操作</button>
          <button type="button" class="btn" @click="contactSupport"><AppIcon name="help" :size="14" />打开帮助</button>
          <button type="button" class="btn" @click="back">保留现场并返回</button>
        </template>
        <template v-else-if="kind === 'maintenance'">
          <button type="button" class="btn btn--primary" :disabled="checking" @click="retry"><AppIcon name="refresh" :size="14" />{{ checking ? '检查中…' : '检查恢复状态' }}</button>
          <button v-if="auth.has('settings.manage')" type="button" class="btn" @click="openGeneralSettings"><AppIcon name="settings" :size="14" />进入系统参数</button>
          <button type="button" class="btn" @click="goHome">返回工作台</button>
          <button type="button" class="btn" @click="contactSupport"><AppIcon name="help" :size="14" />联系支持</button>
        </template>
        <template v-else-if="kind === 'server-error'">
          <button type="button" class="btn btn--primary" :disabled="checking" @click="retry"><AppIcon name="refresh" :size="14" />{{ checking ? '重试中…' : '重试' }}</button>
          <button type="button" class="btn" @click="copyRequest"><AppIcon name="audit" :size="14" />复制请求编号</button>
          <button type="button" class="btn" @click="contactSupport"><AppIcon name="help" :size="14" />联系支持</button>
          <button type="button" class="btn" @click="goHome">返回工作台</button>
        </template>
        <template v-else>
          <button type="button" class="btn btn--primary" @click="goHome">返回工作台</button>
          <button type="button" class="btn" @click="openSearch"><AppIcon name="search" :size="14" />搜索页面</button>
        </template>
      </div>
      <p class="state-card__hint">如果问题持续出现，请把请求编号提供给平台管理员。</p>
    </div>
  </div>
</template>

<style scoped>
.state-page--embedded { min-height: calc(100vh - var(--topbar-h) - var(--tabs-h) - var(--breadcrumb-h) - 48px); }
.state-card__hint { margin-top: 16px; font-size: 10.5px; color: var(--c-faint); }
.state-card__code { display: flex; flex-direction: column; align-items: center; font-size: 44px; }
</style>
